import React, { useState } from 'react';

// ─── Crypto wallet settings ───────────────────────────────
// The user pastes public wallet addresses; the server reads their balances and
// totals them into the on-site wallet.
//
// Only ADDRESSES belong here. An address is a public identifier, the same thing
// you hand someone to receive a payment — it cannot authorise anything. A seed
// phrase or private key would, so the form says so plainly and there is nowhere
// to put one.

// Mirrors the shapes the server validates in services/cryptoBalances.js. The
// check is repeated here to catch a typo while it is still on screen, not to
// be the thing standing between user input and an outbound request.
const EVM_RE    = /^0x[a-fA-F0-9]{40}$/;
const SOLANA_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

const CHAIN_NOTE = {
  evm:    'Ethereum, Polygon, Arbitrum, BNB Chain, Base and OP',
  solana: 'Solana',
};

function addressKind(address) {
  const value = String(address || '').trim();
  if (EVM_RE.test(value))    return 'evm';
  if (SOLANA_RE.test(value)) return 'solana';
  return null;
}

const money = (v) => `$${(Number(v) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function CryptoWalletSettings({ wallets, onChange, onSave, cryptoWallet, onRefresh }) {
  const [label, setLabel]     = useState('');
  const [address, setAddress] = useState('');
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [status, setStatus]   = useState(null); // { ok, message } from the last refresh

  const kind = addressKind(address);

  const add = () => {
    const trimmed = address.trim();
    if (!kind) {
      setError('That does not look like an EVM (0x…) or Solana address.');
      return;
    }
    if (wallets.some(w => w.address.toLowerCase() === trimmed.toLowerCase())) {
      setError('That address is already on the list.');
      return;
    }
    setError('');
    onChange([...wallets, {
      id: crypto.randomUUID(),
      label: label.trim() || null,
      address: trimmed,
      kind,
      addedAt: new Date().toISOString(),
    }]);
    setLabel(''); setAddress('');
  };

  const remove = (id) => onChange(wallets.filter(w => w.id !== id));

  const refresh = async () => {
    if (!onRefresh) { setStatus({ ok: false, message: 'Refresh is not wired up in this view.' }); return; }
    setBusy(true);
    setStatus(null);
    try {
      const result = await onRefresh({ force: true });
      if (result?.ok === false) {
        setStatus({ ok: false, message: result.error || 'The refresh failed.' });
      } else if (result?.skipped === 'no-addresses') {
        setStatus({ ok: false, message: 'No saved addresses on the server yet — click Save Addresses first.' });
      } else if (result?.skipped === 'too-soon') {
        setStatus({ ok: true, message: 'Already read within the last minute, showing that reading.' });
      } else {
        const count = result?.wallets?.length ?? 0;
        setStatus({ ok: true, message: `Read ${count} address${count === 1 ? '' : 'es'} — ${money(result?.wallet?.balance)} counted.` });
      }
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  // Balances are stored per address by the server, so each row can show its own
  // holdings rather than one opaque total.
  const detailFor = (walletAddress) => (cryptoWallet?.detail || [])
    .find(d => String(d.address).toLowerCase() === walletAddress.toLowerCase());

  // Styling comes from ModalTheme.css — this pane lives inside .mdl-body and
  // wears whichever of the two skins the modal is carrying.
  return (
    <div className="mdl-stack gap">
      <div className="mdl-card">
        <div className="mdl-card-title">Wallet addresses</div>
        <div className="mdl-card-hint">
          Paste the public address of any wallet you keep trading money in. We read its balance
          and add it to your on-site wallet: the chain's own coin plus USDT, USDC and DAI.
          <br />
          Only ever paste an <strong>address</strong> — the string you give someone to receive a
          payment. Never a seed phrase or private key. An address cannot move funds, and nothing
          on this page will ever ask you for one that can.
        </div>
      </div>

      {wallets.length > 0 && (
        <div className="mdl-stack">
          {wallets.map(w => {
            const detail = detailFor(w.address);
            return (
              <div key={w.id} className="mdl-card split">
                <div style={{ minWidth: 0 }}>
                  <div className="mdl-card-title">
                    {w.label || short(w.address)}
                    <span className="mdl-tag">{w.kind === 'solana' ? 'Solana' : 'EVM'}</span>
                  </div>
                  <div className="mdl-mono">{w.address}</div>
                  {detail?.holdings?.length > 0 && (
                    <div className="mdl-chips">
                      {detail.holdings.map((h, i) => (
                        <span key={i} className="mdl-chip">
                          {h.chainLabel} · {h.amount.toLocaleString('en-US', { maximumFractionDigits: 6 })} {h.symbol}
                          <span className="mdl-chip-usd">{money(h.usd)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {detail && detail.holdings.length === 0 && (
                    <div className="mdl-card-hint">Nothing found on any chain we check.</div>
                  )}
                </div>
                <div className="mdl-controls" style={{ flexShrink: 0 }}>
                  {detail && <span className="mdl-card-title mdl-ok">{money(detail.usd)}</span>}
                  <button className="mdl-btn danger sm" onClick={() => remove(w.id)}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mdl-card dash">
        <div className="mdl-fields wallet">
          <div>
            <span className="mdl-label">Label (optional)</span>
            <input type="text" className="mdl-input" value={label} onChange={e => setLabel(e.target.value)}
              placeholder="Main wallet" />
          </div>
          <div>
            <span className="mdl-label">Public address</span>
            <input type="text" className="mdl-input mono" value={address}
              onChange={e => { setAddress(e.target.value); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') add(); }}
              placeholder="0x… or a Solana address" />
          </div>
          <button className="mdl-btn primary" onClick={add}>Add</button>
        </div>
        {address.trim() && (
          <div className={`mdl-card-hint ${kind ? 'mdl-ok' : 'mdl-bad'}`}>
            {kind ? `Recognised as ${kind === 'solana' ? 'a Solana' : 'an EVM'} address — we will check ${CHAIN_NOTE[kind]}.`
                  : 'Not a valid address yet.'}
          </div>
        )}
        {error && <div className="mdl-card-hint mdl-bad">{error}</div>}
      </div>

      <div className="mdl-card split">
        <div>
          <div className="mdl-card-title">Counted in your wallet: {money(cryptoWallet?.balance)}</div>
          <div className="mdl-card-hint">
            {cryptoWallet?.updatedAt
              ? `Last read ${new Date(cryptoWallet.updatedAt).toLocaleString()}`
              : 'Not read yet. Save your addresses first, then refresh.'}
          </div>
        </div>
        <div className="mdl-controls">
          <button className="mdl-btn primary" onClick={onSave}>Save Addresses</button>
          <button className="mdl-btn" onClick={refresh} disabled={busy || wallets.length === 0}>
            {busy ? 'Reading chains…' : 'Refresh balances'}
          </button>
        </div>
        {/* Reading every chain for every address takes ten seconds or more, so
            the button says what it is doing and the result is stated when it
            lands. Without this a failure is indistinguishable from a wait. */}
        {busy && (
          <div className="mdl-full mdl-card-hint">
            Checking Ethereum, Polygon, Arbitrum, BNB Chain, Base, OP and Solana. This takes
            about ten seconds per address.
          </div>
        )}
        {status && !busy && (
          <div className={`mdl-full ${status.ok ? 'mdl-ok' : 'mdl-bad'}`}>{status.message}</div>
        )}
      </div>

      {/* Partial failures are named rather than folded into the total. A chain
          that could not be read is money missing from the figure above. */}
      {cryptoWallet?.errors?.length > 0 && (
        <div className="mdl-card warn">
          <div className="mdl-card-title mdl-bad">
            Some sources could not be read, so the total may be short
          </div>
          <ul className="mdl-list" style={{ marginTop: 6 }}>
            {cryptoWallet.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
